import { useState } from "react";
import { UseFormReturn } from "react-hook-form";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Camera, Upload, X, Loader2, User, CreditCard, ScanLine, CheckCircle2 } from "lucide-react";
import { IntakeFormData } from "./IntakeFormSchema";
import { ScanConfirmDialog } from "./ScanConfirmDialog";

interface PhotoUploadSectionProps {
  form: UseFormReturn<IntakeFormData>;
}

interface UploadState {
  isUploading: boolean;
  preview: string | null;
  url: string | null;
}

interface ScanState {
  isScanning: boolean;
  scanned: boolean;
  data: Record<string, unknown> | null;
}

export function PhotoUploadSection({ form }: PhotoUploadSectionProps) {
  const { user } = useAuth();
  const [patientPhoto, setPatientPhoto] = useState<UploadState>({
    isUploading: false,
    preview: null,
    url: null,
  });
  const [insuranceFront, setInsuranceFront] = useState<UploadState>({
    isUploading: false,
    preview: null,
    url: null,
  });
  const [insuranceBack, setInsuranceBack] = useState<UploadState>({
    isUploading: false,
    preview: null,
    url: null,
  });
  const [scanState, setScanState] = useState<ScanState>({
    isScanning: false,
    scanned: false,
    data: null,
  });
  const [showScanConfirm, setShowScanConfirm] = useState(false);

  const scanInsuranceCard = async (imageBase64: string) => {
    setScanState({ isScanning: true, scanned: false, data: null });
    try {
      const { data, error } = await supabase.functions.invoke("scan-insurance-card", {
        body: { imageBase64 },
      });

      if (error) throw error;
      if (!data?.success) throw new Error(data?.error || "Scan failed");

      const extracted = data.data;
      setScanState({ isScanning: false, scanned: false, data: extracted });
      setShowScanConfirm(true);
    } catch (error: any) {
      console.error("Insurance scan error:", error);
      setScanState({ isScanning: false, scanned: false, data: null });
      toast.error(error.message || "Failed to scan insurance card. Please enter details manually.");
    }
  };

  const applyScanData = () => {
    const extracted = scanState.data;
    if (!extracted) return;

    // Insurance type
    if (extracted.insurance_type && ["medicare", "medicaid", "dual_eligible"].includes(extracted.insurance_type as string)) {
      form.setValue("insurance_type", extracted.insurance_type as any, { shouldValidate: true });
    }

    // IDs
    if (extracted.medicare_id) form.setValue("medicare_id", extracted.medicare_id as string);
    if (extracted.medicaid_id) form.setValue("medicaid_id", extracted.medicaid_id as string);

    // Medicare parts
    if (extracted.medicare_part_a !== undefined) form.setValue("medicare_part_a", !!extracted.medicare_part_a);
    if (extracted.medicare_part_b !== undefined) form.setValue("medicare_part_b", !!extracted.medicare_part_b);
    if (extracted.medicare_part_c !== undefined) form.setValue("medicare_part_c", !!extracted.medicare_part_c);
    if (extracted.medicare_part_d !== undefined) form.setValue("medicare_part_d", !!extracted.medicare_part_d);

    // Universal RCM fields
    const stringFields: Array<{ key: string; formField: keyof IntakeFormData }> = [
      { key: "group_id", formField: "group_number" },
      { key: "plan_type", formField: "plan_type" },
      { key: "payer_name", formField: "payer_name" },
      { key: "claims_address", formField: "claims_address" },
      { key: "payer_phone", formField: "payer_phone" },
      { key: "edi_payer_id", formField: "edi_payer_id" },
      { key: "rx_bin", formField: "rx_bin" },
      { key: "rx_pcn", formField: "rx_pcn" },
      { key: "rx_group", formField: "rx_group" },
      { key: "rx_id", formField: "rx_id" },
      { key: "effective_date", formField: "effective_date" },
      { key: "copay_amount", formField: "copay_amount" },
      { key: "deductible_info", formField: "deductible_info" },
      { key: "network_tier", formField: "network_tier" },
    ];

    for (const { key, formField } of stringFields) {
      if (extracted[key]) {
        form.setValue(formField, extracted[key] as string);
      }
    }

    // Member name → first/last name
    if (extracted.member_name) {
      const nameParts = (extracted.member_name as string).split(" ");
      if (nameParts.length >= 2 && !form.getValues("first_name")) {
        form.setValue("first_name", nameParts[0]);
        form.setValue("last_name", nameParts.slice(1).join(" "));
      }
    }

    setScanState((prev) => ({ ...prev, scanned: true }));
    setShowScanConfirm(false);
    toast.success("Scanned data applied to the form. Please review the fields.");
  };

  const discardScanData = () => {
    setScanState({ isScanning: false, scanned: false, data: null });
    setShowScanConfirm(false);
    toast.info("Scan results discarded. Please enter insurance details manually.");
  };

  const uploadFile = async (
    file: File,
    folder: string,
    setState: React.Dispatch<React.SetStateAction<UploadState>>,
    formField: "patient_photo_url" | "insurance_card_front_url" | "insurance_card_back_url"
  ) => {
    if (!user) {
      toast.error("You must be logged in to upload files");
      return;
    }

    if (!file.type.startsWith("image/")) {
      toast.error("Please upload an image file");
      return;
    }

    if (file.size > 5 * 1024 * 1024) {
      toast.error("File size must be less than 5MB");
      return;
    }

    setState((prev) => ({ ...prev, isUploading: true }));

    try {
      const reader = new FileReader();
      const previewPromise = new Promise<string>((resolve) => {
        reader.onload = (e) => resolve(e.target?.result as string);
      });
      reader.readAsDataURL(file);
      const preview = await previewPromise;
      setState((prev) => ({ ...prev, preview }));

      const fileExt = file.name.split(".").pop();
      const fileName = `${user.id}/${folder}/${Date.now()}.${fileExt}`;

      const { error: uploadError } = await supabase.storage
        .from("intake-documents")
        .upload(fileName, file, { upsert: true });

      if (uploadError) throw uploadError;

      const { data: signedUrlData } = await supabase.storage
        .from("intake-documents")
        .createSignedUrl(fileName, 60 * 60 * 24 * 365);

      if (signedUrlData?.signedUrl) {
        setState((prev) => ({ ...prev, url: signedUrlData.signedUrl }));
        form.setValue(formField, signedUrlData.signedUrl);
        toast.success("Photo uploaded successfully");

        // Auto-scan insurance cards (front and back) using base64 preview
        if (formField === "insurance_card_front_url" || formField === "insurance_card_back_url") {
          scanInsuranceCard(preview);
        }
      }
    } catch (error: any) {
      console.error("Upload error:", error);
      toast.error(error.message || "Failed to upload photo");
      setState((prev) => ({ ...prev, preview: null }));
    } finally {
      setState((prev) => ({ ...prev, isUploading: false }));
    }
  };

  const removePhoto = (
    setState: React.Dispatch<React.SetStateAction<UploadState>>,
    formField: "patient_photo_url" | "insurance_card_front_url" | "insurance_card_back_url"
  ) => {
    setState({ isUploading: false, preview: null, url: null });
    form.setValue(formField, "");
    if (formField === "insurance_card_front_url") {
      setScanState({ isScanning: false, scanned: false, data: null });
    }
  };

  const handleFileSelect = (
    e: React.ChangeEvent<HTMLInputElement>,
    folder: string,
    setState: React.Dispatch<React.SetStateAction<UploadState>>,
    formField: "patient_photo_url" | "insurance_card_front_url" | "insurance_card_back_url"
  ) => {
    const file = e.target.files?.[0];
    if (file) {
      uploadFile(file, folder, setState, formField);
    }
  };

  const UploadCard = ({
    title,
    description,
    icon: Icon,
    state,
    setState,
    folder,
    formField,
  }: {
    title: string;
    description: string;
    icon: React.ElementType;
    state: UploadState;
    setState: React.Dispatch<React.SetStateAction<UploadState>>;
    folder: string;
    formField: "patient_photo_url" | "insurance_card_front_url" | "insurance_card_back_url";
  }) => (
    <Card className="p-4">
      <div className="flex items-center gap-2 mb-3">
        <Icon className="h-5 w-5 text-primary" />
        <div className="flex-1">
          <h4 className="font-medium text-foreground">{title}</h4>
          <p className="text-xs text-muted-foreground">{description}</p>
        </div>
        {(formField === "insurance_card_front_url" || formField === "insurance_card_back_url") && scanState.isScanning && (
          <Badge variant="secondary" className="animate-pulse">
            <ScanLine className="h-3 w-3 mr-1" />
            Scanning...
          </Badge>
        )}
        {(formField === "insurance_card_front_url" || formField === "insurance_card_back_url") && scanState.scanned && !scanState.isScanning && (
          <div className="flex items-center gap-1">
            <Badge variant="default" className="bg-primary">
              <CheckCircle2 className="h-3 w-3 mr-1" />
              Scanned
            </Badge>
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="h-6 px-2 text-xs"
              onClick={() => {
                if (state.preview) scanInsuranceCard(state.preview);
              }}
            >
              <ScanLine className="h-3 w-3 mr-1" />
              Re-scan
            </Button>
          </div>
        )}
      </div>

      {state.preview || state.url ? (
        <div className="relative">
          <img
            src={state.preview || state.url || ""}
            alt={title}
            className="w-full h-40 object-cover rounded-lg border"
          />
          <Button
            type="button"
            variant="destructive"
            size="icon"
            className="absolute top-2 right-2 h-8 w-8"
            onClick={() => removePhoto(setState, formField)}
          >
            <X className="h-4 w-4" />
          </Button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center h-40 border-2 border-dashed border-muted-foreground/25 rounded-lg cursor-pointer hover:border-primary/50 transition-colors">
          {state.isUploading ? (
            <div className="flex flex-col items-center gap-2">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
              <span className="text-sm text-muted-foreground">Uploading...</span>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-2">
              <div className="flex gap-2">
                <Camera className="h-6 w-6 text-muted-foreground" />
                <Upload className="h-6 w-6 text-muted-foreground" />
              </div>
              <span className="text-sm text-muted-foreground">
                Take photo or upload
              </span>
              <span className="text-xs text-muted-foreground">
                JPG, PNG up to 5MB
              </span>
              {(formField === "insurance_card_front_url" || formField === "insurance_card_back_url") && (
                <span className="text-xs text-primary font-medium">
                  Auto-scans to fill insurance fields
                </span>
              )}
            </div>
          )}
          <input
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(e) => handleFileSelect(e, folder, setState, formField)}
            disabled={state.isUploading}
          />
        </label>
      )}
    </Card>
  );

  return (
    <div className="space-y-6">
      <div className="border-b pb-2">
        <h3 className="text-lg font-semibold text-foreground">
          Photos & Insurance Card
        </h3>
        <p className="text-sm text-muted-foreground">
          Upload a photo of the patient/loved one and images of your insurance card.
          <span className="text-primary font-medium"> Insurance card front will be automatically scanned to fill in your insurance details.</span>
        </p>
      </div>

      <UploadCard
        title="Patient / Loved One Photo"
        description="A clear photo for identification"
        icon={User}
        state={patientPhoto}
        setState={setPatientPhoto}
        folder="patient-photo"
        formField="patient_photo_url"
      />

      <div className="grid md:grid-cols-2 gap-4">
        <UploadCard
          title="Insurance Card - Front"
          description="Front side — auto-scans to fill insurance info"
          icon={CreditCard}
          state={insuranceFront}
          setState={setInsuranceFront}
          folder="insurance-front"
          formField="insurance_card_front_url"
        />
        <UploadCard
          title="Insurance Card - Back"
          description="Back side of your insurance card"
          icon={CreditCard}
          state={insuranceBack}
          setState={setInsuranceBack}
          folder="insurance-back"
          formField="insurance_card_back_url"
        />
      </div>
      <ScanConfirmDialog
        open={showScanConfirm}
        data={scanState.data}
        onConfirm={applyScanData}
        onCancel={discardScanData}
      />
    </div>
  );
}
